import { useState } from 'react'

import { Box, Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import PremiumModal from './PremiumModal'
import UnlockPremiumButton from './UnlockPremiumButton'
import { UnlockPremiumAction } from './tracking'

const usePremiumPreviewStyles = makeStyles<Theme, { variant: Variant; paddingOffset: number }>((theme) => ({
  container: {
    position: 'relative',
  },
  preview: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    zIndex: 2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,

    // These props let the overlay extend beyond the container to account for padding wrapping the caller.
    boxSizing: 'content-box',
    left: ({ paddingOffset }) => `-${paddingOffset}px`, // Offset the container padding. This is a bit brittle... alternatively, if this becomes a problem, re-arrange padding at the caller.
    border: ({ paddingOffset }) => `${paddingOffset}px solid rgba(255, 255, 255, 0.6)`, // Match the ending fade of the background gradient.

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '0',
      height: '100%',
      width: '100%',
      background: ({ variant }) => {
        // If the container is too small, the gadient ellipsis gets smooshed.
        return variant === 'default'
          ? 'radial-gradient(ellipse,rgb(232, 230, 241) 10%, rgba(255, 255, 255) 60%)'
          : 'rgba(255, 255, 255, 0.6)'
      },
      opacity: 0.4,
    },
  },
  filter: {
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(4),
  },
}))

type Variant = 'default' | 'small'

export function PremiumWrapper({
  enabled,
  children,
  trackingIdPlacement,
  variant = 'default',
  paddingOffset = 24,
}: {
  enabled: boolean
  children: React.ReactNode
  trackingIdPlacement: UnlockPremiumAction
  variant?: Variant
  paddingOffset?: number
}) {
  const classes = usePremiumPreviewStyles({ variant, paddingOffset })
  const [open, setOpen] = useState(false)
  if (enabled) return <>{children}</>
  return (
    <Box className={classes.container}>
      <Box className={classes.preview}>
        <Box>
          <UnlockPremiumButton
            onClick={() => setOpen(true)}
            trackingIdPlacement={trackingIdPlacement}
            paddingOffsetSet={paddingOffset !== 24}
          />
          <PremiumModal
            open={open}
            onCancel={() => setOpen(false)}
            trackingIdPlacement={trackingIdPlacement}
          />
        </Box>
      </Box>
      <Box className={classes.filter}>{children}</Box>
    </Box>
  )
}
